import React from 'react';
import { createPortal } from 'react-dom';
import { UserWithRole, PanelAccess, PanelSection, RoleDefaultAccess } from '../../../../../shared/services/userService';
import { computeEffectivePanelAccess } from '../../../panelAccess/effectivePanelAccess';

interface PanelAccessModalProps {
  user: UserWithRole;
  panelAccess: PanelAccess[];
  roleDefaultAccess: RoleDefaultAccess[];
  panelSections: PanelSection[];
  updating: boolean;
  onToggleAccess: (section: string, hasAccess: boolean) => void;
  onClose: () => void;
}

const PanelAccessModal: React.FC<PanelAccessModalProps> = ({
  user,
  panelAccess,
  roleDefaultAccess,
  panelSections,
  updating,
  onToggleAccess,
  onClose
}) => {
  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="rounded-xl border border-[#243052]/90 bg-[#0a1128] p-6 shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-white mb-2">
          Gérer les accès au panel — {user.username}
        </h3>
        <p className="text-xs text-slate-400 mb-5">
          Les réglages ici sont des <span className="text-slate-200">surcharges individuelles</span> : elles priment sur les accès par défaut du rôle.
        </p>

        {panelSections.length === 0 ? (
          <p className="text-slate-400 text-sm">Chargement des sections...</p>
        ) : (
          <div className="space-y-3">
            {panelSections.map((section) => {
              const hasIndividualRow = panelAccess.some((a) => a.section === section.id);
              const hasAccess = computeEffectivePanelAccess(
                section.id,
                user.roles_id,
                panelAccess,
                roleDefaultAccess,
              );
              return (
                <div
                  key={section.id}
                  className="rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4 flex items-center justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-semibold text-sm">{section.name}</h4>
                    <p className="text-slate-400 text-xs mt-0.5">{section.description}</p>
                    {hasIndividualRow && (
                      <p className="text-sky-400/90 text-xs mt-1">Surcharge individuelle active.</p>
                    )}
                    {panelAccess.find(a => a.section === section.id)?.reason && (
                      <p className="text-slate-500 text-xs mt-1">
                        Raison: {panelAccess.find(a => a.section === section.id)?.reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${
                      hasAccess
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                        : 'bg-red-500/20 text-red-300 border-red-500/40'
                    }`}>
                      {hasAccess ? 'Accordé' : 'Refusé'}
                    </span>
                    <button
                      onClick={() => onToggleAccess(section.id, !hasAccess)}
                      disabled={updating}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                        hasAccess
                          ? 'border-red-500/35 bg-red-500/10 text-red-300 hover:bg-red-500/20'
                          : 'border-emerald-500/35 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                      }`}
                    >
                      {updating ? '...' : hasAccess ? "Retirer" : "Accorder"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-5 w-full rounded-lg border border-white/15 bg-black/30 py-2.5 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
        >
          Fermer
        </button>
      </div>
    </div>,
    document.body
  );
};

export default PanelAccessModal;
